// src/components/common/ErrorBoundary.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Error Boundary component to catch JavaScript errors in children components
 * Prevents the entire app from crashing when a component fails
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Stale deployment: browser cached old index.html whose chunk hashes no longer
    // exist on the server. The server returns a 404 HTML page → MIME mismatch.
    // One automatic reload fetches the new index.html and resolves the issue.
    const isChunkLoadError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Importing a module script failed') ||
      error?.name === 'ChunkLoadError';

    if (isChunkLoadError) {
      const reloadKey = 'hfrc_chunk_reload_at';
      const lastReload = Number(sessionStorage.getItem(reloadKey) || 0);
      const now = Date.now();
      // Guard: only auto-reload once per 30 s to avoid reload loops on genuine errors.
      if (now - lastReload > 30_000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback, resetButtonText } = this.props;

    if (hasError) {
      // Render custom fallback UI if provided, otherwise default error UI
      if (fallback) {
        return fallback;
      }

      return (
        <Paper elevation={3} sx={{ p: 3, m: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            <ErrorOutlineIcon
              color="error"
              // intentional: decorative illustration-size error icon
              sx={{ fontSize: 64, mb: 2 }}
            />
            <Typography variant="h5" color="error" gutterBottom align="center">
              Something went wrong
            </Typography>
            <Typography variant="body1" paragraph align="center" color="text.secondary">
              We encountered an error while trying to display this content.
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={this.handleReset}
              sx={{ mt: 2 }}
            >
              {resetButtonText}
            </Button>
          </Box>
        </Paper>
      );
    }

    // When there's no error, render children normally
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  resetButtonText: PropTypes.string,
  onError: PropTypes.func,
  onReset: PropTypes.func
};

ErrorBoundary.defaultProps = {
  resetButtonText: 'Try Again'
};

export default ErrorBoundary;
